import { useMemo, useState } from 'react';
import type { Task } from '@nepal-football/shared';
import { useDeleteTask, useTasks, useToggleTask } from '../api/hooks';
import { useAdminAuth } from '../hooks/AdminAuthContext';
import TaskItem from '../components/TaskItem';
import TaskForm from '../components/TaskForm';
import AdminGate from '../components/AdminGate';

const PRIORITY_ORDER: Record<Task['priority'], number> = { high: 0, medium: 1, low: 2 };

export default function TaskBoard() {
  const { data: tasks, isLoading, isError } = useTasks();
  const { isAdmin } = useAdminAuth();
  const toggle = useToggleTask();
  const deleteMutation = useDeleteTask();

  const [formMode, setFormMode] = useState<
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'edit'; task: Task }
  >({ kind: 'closed' });

  const sorted = useMemo(() => {
    if (!tasks) return [];
    return [...tasks].sort((a, b) => {
      const p = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      if (p !== 0) return p;
      return (a.sortKey ?? 0) - (b.sortKey ?? 0);
    });
  }, [tasks]);

  if (isLoading) return <div className="inline-loading">Loading tasks…</div>;
  if (isError || !tasks) return <div className="inline-error">Could not load tasks.</div>;

  function handleDelete(task: Task) {
    const ok = window.confirm(`Delete task "${task.task}"?`);
    if (ok) deleteMutation.mutate(task.id);
  }

  return (
    <div className="card">
      <div className="card-header-row">
        <div className="card-title">
          All Volunteer Tasks
          {isAdmin ? ' — Click to Mark Complete' : ' — View Only'}
        </div>
        {isAdmin && (
          <button
            type="button"
            className="reg-btn reg-btn-compact"
            onClick={() => setFormMode({ kind: 'create' })}
          >
            + Add Task
          </button>
        )}
      </div>
      <AdminGate />
      {sorted.map((t) => (
        <TaskItem
          key={t.id}
          task={t}
          onToggle={isAdmin
            ? (task) => toggle.mutate({ id: task.id, done: !task.done })
            : undefined
          }
          onEdit={isAdmin ? (task) => setFormMode({ kind: 'edit', task }) : undefined}
          onDelete={isAdmin ? handleDelete : undefined}
        />
      ))}

      {formMode.kind !== 'closed' && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={formMode.kind === 'edit' ? 'Edit task' : 'Add task'}
          onClick={(e) => {
            if (e.target === e.currentTarget) setFormMode({ kind: 'closed' });
          }}
        >
          <div className="modal-box">
            <TaskForm
              task={formMode.kind === 'edit' ? formMode.task : undefined}
              onSave={() => setFormMode({ kind: 'closed' })}
              onCancel={() => setFormMode({ kind: 'closed' })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
